import { useEffect, useRef } from "react";
import { useWatch } from "react-hook-form";
import PhoneInputField from "../UI/PhoneInputField";
import { useFieldName } from "../rhf/FieldPath";
import { useAppFormContext } from "../rhf/FormProvider";
import { markTouched } from "../rhf/useAppForm";
import { useTranslation } from "react-i18next";
import { phoneDigits } from "utils";

/**
 * Phone number with a country code select. Fields are resolved through the
 * current FieldPath (e.g. a POC group): `countryCode` and `phone_number`.
 * `disableCountryCode`: the country code is shown but can't be changed
 * (its value stays in the form data).
 */
const PhoneInput = ({ disableCountryCode = false } = {}) => {
  const { t } = useTranslation("form");
  const form = useAppFormContext();
  const countryCodeName = useFieldName("countryCode");
  const phoneName = useFieldName("phone_number");
  const countryCode = useWatch({ name: countryCodeName });
  const isFirst = useRef(true);

  // A fixed country code (e.g. Points of Contact): disabled while shown.
  // The form object is not stable between renders, so it is read from a ref
  // (with it in the deps, disabling re-renders and re-runs this forever)
  const formRef = useRef(form);
  formRef.current = form;
  useEffect(() => {
    if (!disableCountryCode) return undefined;
    formRef.current.setFieldDisabled?.(countryCodeName, true);
    return () => formRef.current.setFieldDisabled?.(countryCodeName, false);
  }, [disableCountryCode, countryCodeName]);

  // Changing the country code marks the phone number touched, and drops
  // digits the new country does not have (UA: 9, others: 10)
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    const phone = form.getValues(phoneName);
    const max = phoneDigits(countryCode);
    if (typeof phone === "string" && phone.length > max) {
      form.setValue(phoneName, phone.slice(0, max), { shouldDirty: true });
    }
    markTouched(form, phoneName);
    // the length rule depends on the country code: check the number again
    form.trigger(phoneName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  return (
    <PhoneInputField
      label={t("cellphone")}
      id="phone_number"
      placeholder={t("enterPhoneNumber")}
      autoComplete="phone_number"
      required={true}
      countryCode={countryCode}
    />
  );
};

export default PhoneInput;
